import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Button, DialogActions, DialogContent, DialogTitle, Divider, Stack, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { ArrowDownwardRounded, ArrowUpwardRounded } from "@mui/icons-material";

import TabbedDialog from "components/dialogs/TabbedDialog";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import ColumnManager, { densityOptions } from "../../../tables/columns/ColumnManager";
import { ColumnDialogMode, ColumnDialogParams, ColumnModel, ColumnModelItem, ColumnToolbarType } from "../../../tables/columns/ColumnModel";
import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import StaticTableDialogContent from "components/tables/StaticTableDialogContent";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { AutoWidgetQueryBehavior, WidgetRequest } from "system/widget/Widget";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import { QueryField, QueryFieldTypes, QueryInfo } from "system/query/QueryInfo";
import APIRequest from "system/APIRequest";
import { socialDatasetId } from "system/query/Query";
import RecordTableToolbar from "./RecordTableToolbar";

export type RecordTableSettingsStateType = {
  autoPageSize: boolean;
  customWidgetTitle: string;
  density: string;
  pageSize: number;
  rowStripes?: false,
  widgetTitle: string;
  widgetTitleAlignment: string;
};

export const defaultSettings: RecordTableSettingsStateType = {
  autoPageSize: false,
  customWidgetTitle: "",
  density: "standard",
  pageSize: 25,
  rowStripes: false,
  widgetTitle: "none",
  widgetTitleAlignment: "left",
};

const requestSize = 100;

const defaultWidgetRequest = {
  name: 'record-table',
  endpoint: 'query/records',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultRecordTableRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    records: {
      size: requestSize
    }
  };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}


export default function RecordTableSettings(props: WidgetSettingsProps): React.JSX.Element {
  const { i18n } = useTranslation();

  // Keeping while we decide if settings requesting its own QueryInfo is a permanent feature
  // const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'record-table');
  // const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'record-table');
  // const responseBody = apiResponseInfo?.responseBody;
  // const [requestInfo, setRequestInfo] = useState<any>(originalRequest?.requestInit);
  // const fieldLabels = responseBody?.field_labels ?? [];
  // const fieldTypes = responseBody?.field_types ?? [];

  const [queryInfo, setQueryInfo] = useState<QueryInfo>();
  const [queryFields, setQueryFields] = useState<any>();
  const [sortFieldOptions, setSortFieldOptions] = useState<Array<QueryField>>([]);

  const [settingsState, setSettingsState] = useState<RecordTableSettingsStateType>({...defaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: RecordTableSettingsStateType) => setSettingsState(updatedState);

  const [columnsToAdd, setColumnsToAdd] = useState<ColumnModelItem[] | []>([]);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);

  const initialColumns = queryFields?.reduce((acc: ColumnModelItem[], curr: { label: string, value: string, fieldType: string }) => {
    if (curr.value !== "_id" && curr.value !== "source") { // Don't need "_id" or "source" fields in columns UI
      acc.push({
        id: curr.value,
        field: curr.value,
        headerName: curr.label,
        hidden: false,
        fieldType: curr.fieldType,
        sourceField: curr.label
      });
    }

    return acc;
  }, []);

  const [columnModelState, setColumnModelState] = useState<ColumnModel>({
    columns: initialColumns,
    ...({
      ...props.widget?.data?.columnModel,
      columns: props.widget?.data?.columnModel.columns
        // For widgets that already have the "adult_content" filter column added
        .filter((item: ColumnModelItem) => item.id !== "adult_content")
    }),
  });

  const updateColumnModelState = (newColumnModel: ColumnModel) => setColumnModelState(newColumnModel);

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody: {
      records: {
        size: number,
        sort?: [{ field: string, direction: string }]
      }
    } = {
      records: {
        size: requestSize,
      }
    };

    if (Array.isArray(columnModelState?.sorting?.sortModel) && columnModelState?.sorting?.sortModel?.length > 0) {
      requestInitBody.records.sort = [{
        field: columnModelState.sorting.sortModel[0].field,
        direction: columnModelState.sorting.sortModel[0].sort ?? 'desc'
      }];
    }

    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetDataAndRequests({
      settings: settingsState,
      columnModel: columnModelState,
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }

  function handleColumnDialogMode(params: ColumnDialogParams) {
    if (params.mode === ColumnDialogMode.ADD_COLUMN && "newColumns" in params) {
      setColumnsToAdd(params.newColumns);
    }

    handleOpenDialog();
  }

  function handleHideColumn(columnItem: ColumnModelItem) {
    const indexOfColumnToRemove = columnModelState.columns.findIndex((col: ColumnModelItem) => col.field === columnItem.field);
    const newModelColumnsList = columnModelState.columns.toSpliced(indexOfColumnToRemove, 1);
    const updatedColumnToRemove = { ...columnItem, hidden: true };
    newModelColumnsList.push(updatedColumnToRemove);

    const newColumnModelState = { ...columnModelState, columns: newModelColumnsList };
    setColumnModelState(newColumnModelState);
  }

  function hideMultipleColumns(activeColumns: ColumnModelItem[]) {
    // Filters out active columns to hide while setting "hidden" to true
    // Then adds active columns to back of columns list
    const updatedColumns = columnModelState.columns.filter((col: ColumnModelItem) => {
      if (activeColumns.includes(col)) {
        col.hidden = true;
      }
      return !activeColumns.includes(col);
    });

    const newColumnModelState = {
      ...columnModelState,
      columns: [...updatedColumns, ...activeColumns]
    };
    setColumnModelState(newColumnModelState);
  }

  function handlePrepColumnsToAdd(newColumns: ColumnModelItem[]) {
    setColumnsToAdd(newColumns);
  }

  function handleAddColumns(newColumns?: ColumnModelItem[]) {
    if (newColumns) {
      const filteredColumns = columnModelState.columns.filter((col: ColumnModelItem) => {
        return !newColumns?.includes(col);
      });

      const newColumnsToAdd = newColumns.map((col: ColumnModelItem) => ({ ...col, hidden: false }));
      updateColumnModelState({
        ...columnModelState,
        columns: [...filteredColumns, ...newColumnsToAdd]
      });
    }
  }

  async function getQueryInfo() {
    try {
      const response = await APIRequest("query/info", {}, {
        method: "POST",
        body: JSON.stringify({
          "dataset_id": queryInfo?.dataset.id === socialDatasetId ?
            socialDatasetId : queryInfo?.dataset
        })
      });
      const newQueryInfo = new QueryInfo(response);

      return newQueryInfo;
    } catch(error) {
      console.error("ERROR:", error);
    }
  }

  useEffect(() => {
    if (!queryInfo && !queryFields?.length) {
      getQueryInfo().then((res?: any) => {
        setQueryInfo(res);
        
        const prohibitedFieldIds = ["__count__", "rand", "social_universe", "links_to", "adult_content"];
        let queryFields = res?.fields.reduce((acc: { label: string, value: string, fieldType: string }[], curr: any) => {
          if (prohibitedFieldIds.includes(curr.id) || curr.type === "uid") {
            return acc;
          } else {
            acc.push({ label: curr.label, value: curr.id, fieldType: curr.type });
          }

          return acc;
        }, []);

        // Determine what fields will be available for sorting.
        // Sorting can only be done on certain field types.
        const allowedSortFieldTypes = [
          QueryFieldTypes.decimal, 
          QueryFieldTypes.integer, 
          QueryFieldTypes.timestamp
        ];
        // Some other fields should be omitted for sorting.
        const omittedSortFieldIds = [
          "__count__",
          "rand",
          "social_universe"
        ];
        const sortFieldOptions = res.fields.filter((field: QueryField) => {
          // These are the only fields we can sort on.
          if (omittedSortFieldIds.includes(field.id)) {
            return false;
          }
          return allowedSortFieldTypes.includes(field.type);
        }).sort((a: QueryField, b: QueryField) => {
          if (a.label > b.label) return 1;
          if (a.label < b.label) return -1;
          return 0;
        });

        setSortFieldOptions(sortFieldOptions);
        setQueryFields(queryFields);
      });
    }
    
    if (!columnModelState.columns?.length) {
      setColumnModelState({ columns: initialColumns, ...props.widget?.data?.columnModel });
    }
  }, [queryInfo, queryFields]);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      maxWidth="md"
      open={true}
      style={{ "& .MuiDialogContent-root": { paddingTop: 0 } }}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display"), i18n.t("columns")]}
      title={i18n.t("widget-types.record-table.settings.title")}>
      <>
        {tabValue === 0 &&
          <>
            <SimpleFormLabel>{i18n.t("widget-types.record-table.settings.sorting-title")}</SimpleFormLabel>
            <Autocomplete
              defaultValue={null}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event: React.SyntheticEvent, newSortField) => {
                if (newSortField) {
                  setColumnModelState((prevColumnModelState: ColumnModel) => {
                    return {
                      ...prevColumnModelState,
                      sorting: {
                        sortModel: [{
                          field: newSortField.id,
                          sort: prevColumnModelState.sorting?.sortModel?.[0]?.sort ?? 'desc'
                        }]
                      }
                    };
                  });
                } else {
                  // If sort field is deselected, empty out sort model of columnModel
                  setColumnModelState((prevColumnModelState: ColumnModel) => {
                    return {...prevColumnModelState, sorting: { sortModel: [] }};
                  });
                }
              }}
              options={sortFieldOptions}
              renderInput={params => <TextField {...params} />}
              value={sortFieldOptions?.find((fieldItem: QueryField) => {
                return fieldItem.id === columnModelState.sorting?.sortModel?.[0]?.field;
              }) ?? null}
            />
            {columnModelState.sorting?.sortModel?.[0]?.field &&
              <>
                <SimpleFormLabel>{i18n.t("widget-types.record-table.settings.sorting-direction")}</SimpleFormLabel>
                <ToggleButtonGroup
                  exclusive
                  onChange={(event: React.MouseEvent<HTMLElement>, newSortDirection) => {
                    setColumnModelState((prevColumnModelState: ColumnModel) => {
                      if (!newSortDirection ||
                          !prevColumnModelState.sorting?.sortModel?.[0]?.field
                      ) {
                        return prevColumnModelState;
                      }

                      return {
                        ...prevColumnModelState,
                        sorting: {
                          sortModel: [{
                            field: prevColumnModelState.sorting?.sortModel?.[0].field,
                            sort: newSortDirection
                          }]
                        }
                      };
                    });
                  }}
                  value={columnModelState.sorting?.sortModel?.[0].sort}
                >
                  <ToggleButton value="asc">
                    <ArrowUpwardRounded />
                    {i18n.t("ascending")}
                  </ToggleButton>
                  <ToggleButton value="desc">
                    <ArrowDownwardRounded />
                    {i18n.t("descending")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            }
          </>
        }
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label="Custom Text"
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment}
                />
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("graph")} showBottomDivider>
                <SettingsToggleButton
                  keyid="density"
                  label={i18n.t("tables.row-density")}
                  optionsList={densityOptions}
                  value={settingsState.density} />
                <SettingsCheckbox
                  keyid="rowStripes"
                  label={i18n.t("tables.row-stripes")}
                  checked={settingsState.rowStripes}
                />
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("widgets.settings.pagination.title")}>
                <SettingsSlider
                  keyid="pageSize"
                  label={i18n.t("widgets.settings.pagination.page-size")}
                  min={5}
                  max={100}
                  step={5}
                  sliderValue={settingsState.pageSize}
                  defaultValue={25}
                  showCurrentValue={true}
                />
              </SettingsAccordion>
              <SettingsToggleButton
                keyid="density"
                label={i18n.t("tables.row-density")}
                optionsList={densityOptions}
                value={settingsState.density} />
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
        {tabValue === 2 &&
          <>
            <ColumnManager
              columnModel={columnModelState}
              onColumnDialogMode={handleColumnDialogMode}
              onRemoveColumn={handleHideColumn}
              onRemoveMultipleColumns={hideMultipleColumns}
              toolbarType={ColumnToolbarType.RECORD_TABLE}
              toolbar={
                <RecordTableToolbar
                  hiddenColumns={columnModelState.columns.filter((col: ColumnModelItem) => col.hidden === true)}
                  onColumnDialogAction={handleColumnDialogMode}
                />
              }
              updateColumnModel={updateColumnModelState}
            />
            <ResponsiveDialog open={isDialogOpen} maxWidth="sm">
              <DialogTitle>{i18n.t("tables.add-columns")}</DialogTitle>
              <DialogContent>
                <StaticTableDialogContent
                  columnModelState={columnModelState}
                  onColumnsToAdd={handlePrepColumnsToAdd}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={handleCloseDialog}
                >
                  {i18n.t("cancel")}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => {
                    handleAddColumns(columnsToAdd);
                    handleCloseDialog();
                  }}
                >
                  {i18n.t("add")}
                </Button>
              </DialogActions>
            </ResponsiveDialog>
          </>
        }
      </>
    </TabbedDialog>
  );
}
